import { createContext, useContext } from "react";
import { readErrorMessage, readFieldIssues, type FieldIssue } from "./apiError";
import { API_URL } from "./auth";
import { detectLocale } from "./i18n";

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public issues: FieldIssue[] = [],
  ) {
    super(message);
  }
}

/** JSON fetch against the API with the session cookie; throws ApiError with the server's `error` code. */
export async function api<T>(path: string, init: RequestInit & { json?: unknown } = {}): Promise<T> {
  const { json, ...rest } = init;
  const res = await fetch(`${API_URL}${path}`, {
    credentials: "include",
    ...rest,
    headers: {
      "accept-language": detectLocale(),
      ...(json !== undefined ? { "content-type": "application/json" } : {}),
      ...(rest.headers ?? {}),
    },
    body: json !== undefined ? JSON.stringify(json) : rest.body,
  });
  if (!res.ok) {
    let msg = `${res.status}`;
    let issues: FieldIssue[] = [];
    try {
      const body: unknown = await res.json();
      issues = readFieldIssues(body);
      const text = readErrorMessage(body, "");
      if (text) msg = text;
    } catch {
      /* non-JSON error */
    }
    throw new ApiError(res.status, msg, issues);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

/**
 * Which data the console is looking at. Staff are fixed to their church; superadmins pick
 * "global" or a church id. Both query params the API understands are derived from it.
 */
export interface Scope {
  /** null = global */
  churchId: string | null;
  churches: Array<{ id: string; name: string; slug: string; status: string }>;
  setChurchId: (id: string | null) => void;
  canPickScope: boolean;
}

export const ScopeContext = createContext<Scope>({ churchId: null, churches: [], setChurchId: () => {}, canPickScope: false });
export const useScope = () => useContext(ScopeContext);

/** `?scope=` for /admin/content/*, `?church=` for /admin/church|questions|today. Empty for staff. */
export function scopeQuery(scope: Scope, key: "scope" | "church", extra: Record<string, string | undefined> = {}): string {
  const q = new URLSearchParams();
  if (scope.canPickScope) q.set(key, scope.churchId ?? "global");
  for (const [k, v] of Object.entries(extra)) if (v !== undefined) q.set(k, v);
  const s = q.toString();
  return s ? `?${s}` : "";
}

export type Localized = Record<string, string>;

export function hasLocalizedText(value: Localized | null | undefined): boolean {
  return Boolean(value?.de?.trim() || value?.en?.trim());
}

/** API localized jsonb requires `de`; mirror English when staff only fill the EN column. */
export function normalizeLocalizedRequired(value: Localized): { de: string; en?: string } | null {
  const de = value.de?.trim() ?? "";
  const en = value.en?.trim() ?? "";
  if (!de && !en) return null;
  const out: { de: string; en?: string } = { de: de || en };
  if (en) out.en = en;
  return out;
}

export function fmtDate(iso: string | Date | null | undefined, locale: string, opts: Intl.DateTimeFormatOptions = { dateStyle: "medium" }): string {
  if (!iso) return "";
  return new Intl.DateTimeFormat(locale === "de" ? "de-DE" : "en-GB", opts).format(new Date(iso));
}

export function relTime(iso: string | Date, locale: string): string {
  const diff = (new Date(iso).getTime() - Date.now()) / 1000;
  const rtf = new Intl.RelativeTimeFormat(locale === "de" ? "de" : "en", { numeric: "auto" });
  const abs = Math.abs(diff);
  if (abs < 3600) return rtf.format(Math.round(diff / 60), "minute");
  if (abs < 86_400) return rtf.format(Math.round(diff / 3600), "hour");
  return rtf.format(Math.round(diff / 86_400), "day");
}

export function isoDay(d: Date): string {
  return d.toISOString().slice(0, 10);
}

export function addDays(day: string, n: number): string {
  const d = new Date(`${day}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return isoDay(d);
}
